"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { styled } from "@mui/material/styles";
import Tooltip, { tooltipClasses, TooltipProps } from "@mui/material/Tooltip";
import Image from "next/image";
import { ReactNode, useEffect, useState } from "react";
import { Controller, ControllerRenderProps, FormProvider, Resolver, useForm } from "react-hook-form";
import { EventUpdateTitle } from "@/app/(main)/(club)/mes-clubs/[id]/components/modal/event/update/event-update-title";
import { editEventSchema } from "@/app/(main)/(club)/mes-clubs/[id]/components/modal/event/update/schema";
import { SelectChips } from "@/app/(main)/(club)/mes-clubs/[id]/components/modal/event/update/select-chips";
import { useAvailableMembers } from "@/app/(main)/(club)/mes-clubs/[id]/hooks/use-available-members";
import { FormActions } from "@/app/(security)/inscription/components/form-actions";
import ComponentLoading from "@/components/global/component-loading";
import { FormSelect } from "@/components/global/form/inputs/form-select";
import { FormTextArea } from "@/components/global/form/inputs/form-textarea";
import { ModalSkeleton } from "@/components/modal/modal-skeleton";
import { useEventDocumentMutation } from "@/mutations/use-event-document-mutation";
import { useEventMutation } from "@/mutations/use-event-mutation";
import { useEventQuery } from "@/queries/event/event-queries";
import { useModalStore } from "@/stores/modal-store";
import { EventEnum } from "@/TS/Enums/global_enums";
import { DocumentUploader } from "../../../mon-profil/parametres/components/document-uploader";
import { DeleteEventDocument } from "./modal/event/delete-document/delete-document";
import { useFlashMessage } from "./use-flash-message";
const CustomTooltip = styled(({
  className,
  ...props
}: TooltipProps) => <Tooltip arrow {...props} classes={{
  popper: className
}} />)(({
  theme
}) => ({
  zIndex: 9999,
  [`& .${tooltipClasses.arrow}`]: {
    color: theme.palette.common.black
  },
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.common.black,
    zIndex: 9999
  }
}));
export interface EditEventFormData {
  agenda: string;
  absentMembers: Member[];
}
interface EventProps {
  type?: EventEnum;
  clubSlug?: string;
  startDate?: string;
}
interface EditEventModalContentProps {
  eventProps: EventProps;
}
export interface Member {
  "@id": string;
  firstName: string;
  lastName: string;
}
export default function EditEventModalContent({
  eventProps
}: EditEventModalContentProps) {
  const {
    modalConfig,
    close
  } = useModalStore();
  const eventMutation = useEventMutation();
  const eventDocumentMutation = useEventDocumentMutation();
  const {
    showFlashMessage
  } = useFlashMessage();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [eventDocument, setEventDocument] = useState<string | null>(null);
  const [pendingUpload, setPendingUpload] = useState<boolean>(false);
  const skeletonOptions = modalConfig?.modalOptions?.loader?.skeleton;
  const loaderComponent = modalConfig?.modalOptions?.loader?.component;
  const {
    data: event,
    isLoading: isEventLoading
  } = useEventQuery({
    type: eventProps.type,
    clubSlug: eventProps.clubSlug,
    startDate: eventProps.startDate?.split("T")[0]
  });
  const isFileValid = (file: File) => {
    if (file.size > 4000000) {
      return false;
    }
    if (file.type !== "application/pdf") {
      return false;
    }
    return true;
  };
  useEffect(() => {
    if (selectedFile) {
      if (!isFileValid(selectedFile)) {
        showFlashMessage("Le format du fichier doit être .pdf et la taille doit être inférieure à 4Mo", "danger", "mes-clubs-calendar");
      }
    }
  }, [selectedFile, showFlashMessage]);
  useEffect(() => {
    setEventDocument(event?.documentReportUrl || null);
  }, [event]);
  const methods = useForm<EditEventFormData>({
    mode: "onSubmit",
    resolver: zodResolver(editEventSchema) as Resolver<EditEventFormData>,
    defaultValues: {
      agenda: "",
      absentMembers: []
    },
    values: event ? {
      agenda: event.agenda || "",
      absentMembers: event.absentMembers?.map((member: Member) => ({
        "@id": member["@id"],
        firstName: member.firstName,
        lastName: member.lastName
      })) || []
    } : undefined
  });
  const {
    handleSubmit,
    formState: {
      errors,
      isSubmitted,
      isDirty
    },
    control,
    watch
  } = methods;
  const absentMembers = watch("absentMembers");
  const onSubmit = async (data: EditEventFormData) => {
    try {
      setPendingUpload(true);
      if (selectedFile) {
        const formData = new FormData();
        formData.append("file", selectedFile);
        formData.append("name", "Compte rendu");
        formData.append("type", "application/pdf");
        formData.append("size", selectedFile.size.toString());
        formData.append("mimeType", selectedFile.type);
        formData.append("createdAt", new Date().toISOString());
        await eventDocumentMutation.mutateAsync({
          formData,
          type: eventProps.type!,
          clubSlug: eventProps.clubSlug!,
          startDate: eventProps.startDate!.split("T")[0]
        });
        showFlashMessage("Le compte rendu a été mis en ligne avec succès", "success2", "mes-clubs-calendar");
        setSelectedFile(null);
      }
      if (!isDirty && !selectedFile) {
        showFlashMessage("Aucune modification apportée", "warning2", "mes-clubs-calendar");
        setPendingUpload(false);
        close();
        return;
      }
      const transformedData = {
        agenda: data.agenda,
        absentMembers: data.absentMembers.map(member_0 => member_0["@id"])
      };
      await eventMutation.mutateAsync({
        data: transformedData,
        type: eventProps.type!,
        clubSlug: eventProps.clubSlug!,
        startDate: eventProps.startDate!.split("T")[0]
      });
      setPendingUpload(false);
      close();
    } catch (error) {
      console.error("Erreur lors de la modification:", error);
      setPendingUpload(false);
    }
  };
  const handleMemberSelect = (e: React.ChangeEvent<HTMLSelectElement>, field: ControllerRenderProps<EditEventFormData, "absentMembers">) => {
    const member_1 = event?.attendedMembers?.find(m => m["@id"] === e.target.value);
    if (member_1 && !field.value.find(m_0 => m_0["@id"] === member_1["@id"])) {
      field.onChange([...field.value, member_1]);
    }
  };
  const availableMembers = useAvailableMembers(event?.attendedMembers, absentMembers);
  if (isEventLoading) {
    if (!skeletonOptions) {
      return <div className="w-full h-[200px] flex items-center justify-center transition-[max-height] duration-300 ease-in-out max-h-[200px]">
          {loaderComponent as ReactNode}
        </div>;
    }
    return <ModalSkeleton className={skeletonOptions?.className} header={skeletonOptions?.header} title={skeletonOptions?.title} content={skeletonOptions?.content} />;
  }
  return <div className="transition-[max-height] duration-300 ease-in-out max-h-[1000px]">
      <div className="p-6">
        <EventUpdateTitle event={event} />
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <div className="flex flex-col p-8 rounded-lg bg-event bg-opacity-5">
              <FormTextArea name="agenda" label="Ordre du jour" error={errors.agenda?.message} isValid={isSubmitted && !errors.agenda} placeholder="Saisir l'ordre du jour" />

              <div className="space-y-2">
                <Controller name="absentMembers" control={control} render={({
                field: field_0
              }) => <div className="space-y-2 mt-4">
                      <FormSelect name="memberSelect" label="Notifier des absents" placeholder="Sélectionner un membre..." value="" isValid={isSubmitted && !errors.absentMembers} onChange={e_0 => handleMemberSelect(e_0, field_0)}>
                        {availableMembers}
                      </FormSelect>
                      <div className="mb-8">
                        <SelectChips value={absentMembers} onChange={newValue => {
                    methods.setValue("absentMembers", newValue);
                  }} />
                      </div>
                    </div>} />
              </div>

              <div className="flex flex-col gap-2 relative">
                {eventDocument && <div className="absolute top-10 right-2 flex gap-4 z-10">
                    <CustomTooltip title="Visualiser le document" placement="top">
                      <div className="cursor-pointer" onClick={() => {
                    window.open(eventDocument as string, "_blank");
                  }}>
                        <Image src="/icons/view-icon.png" alt="Visualiser" width={24} height={24} className="w-6 h-6" />
                      </div>
                    </CustomTooltip>
                    <CustomTooltip title="Supprimer le document" placement="top">
                      <div className="cursor-pointer" onClick={() => {
                    useModalStore.getState().open({
                      component: <DeleteEventDocument clubSlug={eventProps.clubSlug!} type={eventProps.type!} startDate={eventProps.startDate!} onClose={() => {
                        useModalStore.getState().close();
                      }} />,
                      modalOptions: {
                        closeOnOverlayClick: false,
                        loader: {
                          component: <ComponentLoading />
                        },
                        size: {
                          width: "w-[50rem]"
                        }
                      },
                      layoutOptions: {
                        showCloseButton: false
                      }
                    });
                  }}>
                        <Image src="/icons/icon_cancel.svg" alt="Supprimer" width={24} height={24} className="w-6 h-6" />
                      </div>
                    </CustomTooltip>
                  </div>}
                <p style={{
                fontSize: "16px"
              }}>Compte rendu de réunion</p>
                <DocumentUploader disabled={!!eventDocument} documentName="Compte rendu" existingDocument={eventDocument ? {
                id: eventDocument + "_" + event?.startDate,
                name: "Compte rendu du " + event?.startDate?.split("T")[0] || "",
                url: event?.documentReportUrl || ""
              } : undefined} onSuccess={() => {}} onError={() => {}} selectedFile={selectedFile} setSelectedFile={setSelectedFile} />
              </div>
            </div>
            {/* FORM ACTIONS */}
            <FormActions style={{
            border: "border-0"
          }} isPending={eventMutation.isPending || pendingUpload} closeModal={close} />
          </form>
        </FormProvider>
      </div>
    </div>;
}